import { supabase } from "@/integrations/supabase/client";

/**
 * Fuentes del selector "Ver como" del Portal del Personal.
 *
 * Hay dos, según quién mira:
 *
 *   1. `fetchUsuariosSistema` — Super Administrador. Ve y busca a CUALQUIER
 *      usuario del sistema (~1.9k activos), para comprobar cómo se vería el
 *      portal para cada uno. Al ser tantos, la búsqueda va contra el servidor:
 *      nunca se traen todos de golpe.
 *   2. `fetchPersonalConCuenta` — el resto de roles con `puede_impersonar`.
 *      Sólo personal de la organización con cuenta del sistema ligada
 *      (`personal_organizacional.email_usuario` → `usuarios.email`, el campo
 *      "Cuenta del sistema" de Estructura de comisiones → Roles y sueldos).
 *
 * OJO con `usuarios`: su llave es el EMAIL, no hay columna `id`. Pedirla hacía
 * fallar la consulta entera y por eso el selector salía vacío.
 */
export interface PersonalConCuenta {
  /** Identidad estable del usuario: su email (llave de `usuarios`). */
  id: string;
  personalId: number | null;
  nombre: string;
  email: string;
  rolId: number | null;
  rolNombre: string;
  personaId: number | null;
  authUserId: string | null;
  tipoPersonal: string | null;
}

const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();

/** `%` y `_` son comodines de LIKE: escaparlos evita búsquedas que traen de más. */
const escapeLike = (v: string) => v.replace(/[%_]/g, (c) => `\${c}`);

const USUARIO_COLS = "email, nombre, rol_id, id_persona, auth_user_id, roles(nombre)";

/**
 * Menú "Portal del personal" en `menus`: por id (igual en dev y prod, verificado el 2026-10-20),
 * nunca por nombre, que se edita en Administrar menús.
 */
const MENU_PORTAL_PERSONAL_ID = 39;
/** Super Administrador: se salta la individualización (igual que `get_menus_bloqueados`). */
const ROL_SUPER_ADMIN = 1;

/**
 * Quién puede entrar hoy al Portal del Personal, con la MISMA regla que aplica
 * el sistema al pintar el menú:
 *
 *   1. el rol tiene permisos en algún submenú activo del portal, y
 *   2. si el portal está marcado "individualizar por usuario", además hay que
 *      estar en su lista (`menus_usuarios_acceso`) — salvo el Super Administrador,
 *      que `get_menus_bloqueados()` exceptúa a propósito.
 *
 * Se resuelve en varios pasos y no con un join embebido: el filtro sobre una
 * tabla anidada de PostgREST no filtra y devuelve vacío sin error (patrón #1 del
 * CLAUDE.md).
 */
export interface AccesoPortalPersonal {
  /** Roles con permisos en el portal. Vacío = nadie por rol. */
  roles: number[];
  /**
   * Correos habilitados uno por uno. `null` cuando el portal NO se individualiza
   * (entonces basta con el rol).
   */
  emails: Set<string> | null;
}

let accesoCache: Promise<AccesoPortalPersonal> | null = null;

export async function fetchAccesoPortalPersonal(): Promise<AccesoPortalPersonal> {
  if (accesoCache) return accesoCache;
  accesoCache = (async () => {
    const { data: menu } = await (supabase as any)
      .from("menus")
      .select("id, individualizar_por_usuario")
      .eq("id", MENU_PORTAL_PERSONAL_ID)
      .eq("activo", true)
      .maybeSingle();
    if (!menu) return { roles: [], emails: null };

    const { data: submenus } = await (supabase as any)
      .from("submenus")
      .select("id")
      .eq("menu_id", menu.id)
      .eq("activo", true);
    const submenuIds = ((submenus as any[]) ?? []).map((s) => Number(s.id));
    if (!submenuIds.length) return { roles: [], emails: null };

    const { data: permisos } = await (supabase as any)
      .from("submenus_permisos")
      .select("rol_id")
      .eq("activo", true)
      .in("submenu_id", submenuIds);
    const roles = [...new Set(((permisos as any[]) ?? []).map((p) => Number(p.rol_id)))];

    if (!menu.individualizar_por_usuario) return { roles, emails: null };

    const { data: filas } = await (supabase as any)
      .from("menus_usuarios_acceso")
      .select("usuario_id")
      .eq("menu_id", menu.id)
      .eq("activo", true);
    const emails = new Set(
      ((filas as any[]) ?? []).map((f) => norm(f.usuario_id)).filter(Boolean),
    );
    return { roles, emails };
  })();
  return accesoCache;
}

/** La lista de acceso cambia al tocar permisos o individualización. */
export function invalidarAccesoPortalPersonal() {
  accesoCache = null;
}

/** ¿Este usuario entra hoy al portal? */
function tieneAccesoPortalPersonal(u: { email: string; rolId: number | null }, acceso: AccesoPortalPersonal) {
  if (u.rolId == null || !acceso.roles.includes(u.rolId)) return false;
  if (!acceso.emails) return true;
  return u.rolId === ROL_SUPER_ADMIN || acceso.emails.has(norm(u.email));
}

function mapUsuario(u: any, tipoPersonal: string | null = null, personalId: number | null = null): PersonalConCuenta {
  return {
    id: u.email as string,
    personalId,
    nombre: (u.nombre as string) || (u.email as string),
    email: u.email as string,
    rolId: (u.rol_id as number) ?? null,
    rolNombre: (u.roles?.nombre as string) ?? "Usuario",
    personaId: (u.id_persona as number) ?? null,
    authUserId: (u.auth_user_id as string) ?? null,
    tipoPersonal,
  };
}

/**
 * Usuarios que HOY entran al Portal del Personal, filtrados en el servidor por
 * nombre o correo. Sin término se devuelve la primera página alfabética, para
 * que el desplegable nunca aparezca vacío.
 *
 * Antes listaba a cualquier usuario activo del sistema —inmobiliarias, clientes,
 * agentes externos—, y "ver como" alguien que no puede abrir el portal no
 * enseña nada: la vista sale vacía y no se distingue de un error.
 */
export async function fetchUsuariosSistema(
  termino = "",
  limite = 50,
): Promise<PersonalConCuenta[]> {
  const q = termino.trim();
  const acceso = await fetchAccesoPortalPersonal();
  if (acceso.roles.length === 0) return [];

  let query = (supabase as any)
    .from("usuarios")
    .select(USUARIO_COLS)
    .eq("activo", true)
    .in("rol_id", acceso.roles)
    .order("nombre", { ascending: true, nullsFirst: false })
    .limit(limite);

  // Portal individualizado: además del rol hay que estar en su lista. El Super
  // Administrador queda exento, igual que en `get_menus_bloqueados()`.
  if (acceso.emails) {
    const lista = [...acceso.emails];
    query = lista.length
      ? query.or(
          `rol_id.eq.${ROL_SUPER_ADMIN},email.in.(${lista.map((e) => `"${e}"`).join(",")})`,
        )
      : query.eq("rol_id", ROL_SUPER_ADMIN);
  }

  if (q) {
    const patron = `%${escapeLike(q)}%`;
    query = query.or(`nombre.ilike.${patron},email.ilike.${patron}`);
  }

  const { data, error } = await query;
  if (error || !data?.length) return [];

  // El "tipo de personal" (Empleado REV, etc.) es un dato extra: si no está, el
  // usuario igual se puede seleccionar.
  const emails = (data as any[]).map((u) => u.email).filter(Boolean);
  const tipoPorEmail = await fetchTipoPersonalPorEmail(emails);

  return (data as any[]).map((u) => {
    const extra = tipoPorEmail.get(norm(u.email));
    return mapUsuario(u, extra?.tipoPersonal ?? null, extra?.personalId ?? null);
  });
}

/** Personal de la organización con cuenta del sistema Y acceso al portal. */
export async function fetchPersonalConCuenta(): Promise<PersonalConCuenta[]> {
  const acceso = await fetchAccesoPortalPersonal();
  if (acceso.roles.length === 0) return [];
  const { data: personal, error } = await (supabase as any)
    .from("personal_organizacional")
    .select("id, nombre, email_usuario, tipo_personal")
    .eq("activo", true)
    .not("email_usuario", "is", null)
    .order("nombre");
  if (error || !personal?.length) return [];

  const emails = Array.from(
    new Set((personal as any[]).map((p) => norm(p.email_usuario)).filter(Boolean)),
  );
  if (!emails.length) return [];

  // PostgREST no puede unir por email, así que se resuelve en dos consultas y se
  // compara en minúsculas + trim, como el resto del proyecto.
  const { data: usuarios } = await (supabase as any)
    .from("usuarios")
    .select(USUARIO_COLS)
    .in("email", emails);

  const porEmail = new Map<string, any>();
  for (const u of (usuarios as any[]) ?? []) porEmail.set(norm(u.email), u);

  return (personal as any[])
    .map((p) => {
      const u = porEmail.get(norm(p.email_usuario));
      if (!u) return null;
      const mapped = mapUsuario(u, (p.tipo_personal as string) ?? null, (p.id as number) ?? null);
      // Tener cuenta no basta: se lista a quien realmente abre el portal.
      if (!tieneAccesoPortalPersonal(mapped, acceso)) return null;
      // En este listado manda el nombre del directorio de personal.
      return { ...mapped, nombre: (p.nombre as string) || mapped.nombre };
    })
    .filter((v): v is PersonalConCuenta => v !== null);
}

async function fetchTipoPersonalPorEmail(
  emails: string[],
): Promise<Map<string, { tipoPersonal: string | null; personalId: number | null }>> {
  const out = new Map<string, { tipoPersonal: string | null; personalId: number | null }>();
  if (!emails.length) return out;

  // `.in()` compara exacto: se mandan también en minúsculas porque el correo
  // capturado en el directorio de personal no siempre respeta el mismo case.
  const variantes = Array.from(new Set([...emails, ...emails.map((e) => e.toLowerCase())]));
  const { data } = await (supabase as any)
    .from("personal_organizacional")
    .select("id, email_usuario, tipo_personal")
    .in("email_usuario", variantes);

  for (const p of (data as any[]) ?? []) {
    const key = norm(p.email_usuario);
    if (!key || out.has(key)) continue;
    out.set(key, {
      tipoPersonal: (p.tipo_personal as string) ?? null,
      personalId: (p.id as number) ?? null,
    });
  }
  return out;
}
